import { darken } from 'polished';
import React, { FC, ReactNode, useMemo } from 'react';
import { Colors } from '../../../ui';
import { Pose } from '../../canvas/visualization/Pose';
import { RenderBounds, RenderBox, RenderInner } from '../../canvas/visualization/RenderHelpers';
import { RenderFencedContent } from './RenderFencedContent';
import { TextGenerator, TextGeneratorComplete, TextGeneratorInboundValue } from './TextGenerator';
import { TextGeneratorLayout } from './TextGeneratorLayout';

const useBorderFromPose = (pose: Pose, background: string) => {
    return useMemo(() => {
        const { selected, targeted, pressed, beingEdited } = pose;
        if (beingEdited) return `solid 2px ${Colors.primary}`;
        if (!selected && pressed) return `solid 2px ${Colors.primaryDark}`;
        if (!selected && targeted) return `solid 2px ${Colors.primary}`;
        if (!background) return 'solid 2px transparent';
        return `solid 2px ${darken(.15, background)}`;
    }, [pose, background]);
};

const RenderDesign = React.memo<{ layout: TextGeneratorLayout, border: string }>(({ layout, border }) => {

    const style = {
        ...layout.base.toCSS(),
        backgroundColor: layout.background || 'transparent',
        border,
        borderRadius: '4px',
    };

    return <RenderBox {...style} />;
});

export const TextGeneratorVisual: FC<{ object: TextGenerator, children?: ReactNode }> = ({ object }) => {
    const { layout } = object;
    const { beingEdited } = Pose.get(object);
    const value = TextGeneratorInboundValue.get(object) || '';
    const complete = TextGeneratorComplete.get(object) || false;
    const border = useBorderFromPose(Pose.get(object), object.background);
    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderDesign layout={layout} border={border} />
                <RenderBox inset="4px">
                    {!beingEdited && <RenderFencedContent content={value} complete={complete} />}
                </RenderBox>
            </RenderInner>
        </RenderBounds>
    </>;
};
